import { useEffect, useMemo, useState } from "react";
import { buildComposeUrl, fetchLayeredScene } from "../../api/layeredSceneClient";
import type { LayeredSceneResponse, LayeredSceneSurfaceDto } from "../../types/layeredScene";

export interface LayeredScenePreviewToolProps {
  /** Scene GUID (preferred — survives slug edits) or slug. */
  idOrSlug: string;
  /** Same-origin if omitted. */
  baseUrl?: string;
  /** DVar language id; defaults server-side to 1 when omitted. */
  lang?: number;
  /**
   * Author-only flag for in-dashboard previews. When true, the server returns
   * unpublished materials and non-Ready layers too. Server only honors it for
   * dashboard-authenticated requests; anonymous embeds always see the public view.
   */
  includeUnpublished?: boolean;
}

/**
 * React equivalent of the old LayeredScenePreview.razor modal. Single source of
 * truth for both the public visualizer-style preview and the in-dashboard author
 * preview — the only difference is the includeUnpublished flag.
 *
 * The composed image is just an <img> pointing at the compose endpoint. The
 * compositor only renders Ready layers, so picks against unpublished/non-Ready
 * materials degrade gracefully (they don't change the image, but the picker
 * still shows them so authors can see what's missing).
 */
export function LayeredScenePreviewTool(props: LayeredScenePreviewToolProps) {
  const [data, setData] = useState<LayeredSceneResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});

  // ── Fetch scene once on mount / when params change ──────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchLayeredScene({
      baseUrl: props.baseUrl,
      idOrSlug: props.idOrSlug,
      lang: props.lang,
      includeUnpublished: props.includeUnpublished,
      signal: ctrl.signal,
    })
      .then(resp => {
        setData(resp);
        // Seed choices: pick the explicit IsDefault on each surface, or the first
        // entry that's renderable, or the first entry overall (Blazor preview did
        // the same — prefer default, fall back to "first available").
        const seed: Record<string, string> = {};
        for (const surface of resp.surfaces) {
          if (surface.materials.length === 0) continue;
          const explicit = surface.materials.find(m => m.isDefault);
          const renderable = surface.materials.find(m => m.isRenderable !== false);
          const pick = explicit ?? renderable ?? surface.materials[0];
          seed[surface.collectionId] = pick.materialId;
        }
        setChoices(seed);
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setError(err.message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [props.baseUrl, props.idOrSlug, props.lang, props.includeUnpublished]);

  // ── Compose URL (or base image fallback before any picks) ───────────
  const composeSrc = useMemo(() => {
    if (!data) return null;
    if (Object.keys(choices).length === 0) return data.baseImageUrl;
    return buildComposeUrl({
      baseUrl: props.baseUrl,
      idOrSlug: data.id || props.idOrSlug, // prefer GUID echoed back by the server
      choices,
    });
  }, [data, choices, props.baseUrl, props.idOrSlug]);

  if (loading && !data) {
    return <div className="dptp-state dptp-state--loading">Loading scene…</div>;
  }
  if (error) {
    return <div className="dptp-state dptp-state--error">Error: {error}</div>;
  }
  if (!data) {
    return <div className="dptp-state dptp-state--empty">No data.</div>;
  }

  return (
    <div className="lsp">
      <div className="lsp__row">
        {/* Composed image (left) */}
        <div className="lsp__image-card">
          {composeSrc ? (
            <img src={composeSrc} alt={data.name} className="lsp__image" />
          ) : (
            <div className="lsp__image-empty">No base image and no picks yet.</div>
          )}
        </div>

        {/* Material picker (right) */}
        <div className="lsp__choices">
          <header className="lsp__header">
            <h2 className="lsp__title">{data.name}</h2>
          </header>

          {data.surfaces.length === 0 ? (
            <p className="dptp__muted">This scene has no layers yet.</p>
          ) : (
            data.surfaces.map(surface => (
              <SurfacePicker
                key={surface.collectionId}
                surface={surface}
                selectedMaterialId={choices[surface.collectionId]}
                onPick={materialId =>
                  setChoices(prev => ({ ...prev, [surface.collectionId]: materialId }))
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function SurfacePicker(props: {
  surface: LayeredSceneSurfaceDto;
  selectedMaterialId: string | undefined;
  onPick: (materialId: string) => void;
}) {
  const { surface, selectedMaterialId, onPick } = props;

  if (surface.materials.length === 0) {
    return (
      <section className="dptp__decision">
        <h6 className="dptp__decision-heading">{surface.label}</h6>
        <span className="dptp__muted dptp__muted--small">No materials yet.</span>
      </section>
    );
  }

  return (
    <section className="dptp__decision">
      <h6 className="dptp__decision-heading">{surface.label}</h6>
      <div className="lsp__swatches">
        {surface.materials.map(m => {
          const isSelected = selectedMaterialId === m.materialId;
          // isPublished / isRenderable are only present in dashboard responses
          // (includeUnpublished=true). When missing, treat as a normal public entry.
          const unpublished = m.isPublished === false;
          const notRenderable = m.isRenderable === false;
          const classes = [
            "lsp__swatch",
            isSelected ? "lsp__swatch--selected" : "",
            notRenderable ? "lsp__swatch--unrenderable" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              key={m.materialId}
              type="button"
              className={classes}
              onClick={() => onPick(m.materialId)}
              title={[
                m.name,
                m.skuCode ? `SKU: ${m.skuCode}` : null,
                unpublished ? "Unpublished" : null,
                notRenderable ? `Not renderable (${m.layerStatus ?? "no layer"})` : null,
              ].filter(Boolean).join("\n")}
            >
              {m.thumbnailUrl ? (
                <img src={m.thumbnailUrl} alt={m.name} className="lsp__swatch-img" />
              ) : (
                <div className="lsp__swatch-img lsp__swatch-img--placeholder" />
              )}
              <span className="lsp__swatch-label">{m.name}</span>
              {(unpublished || notRenderable) && (
                <span className="lsp__swatch-badge">
                  {unpublished ? "Draft" : (m.layerStatus ?? "Pending")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default LayeredScenePreviewTool;
